import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { LinkButton } from 'src/components/atoms';
import { FormInput } from 'src/components/molecules/FormInput';
import { SpinnerButton } from 'src/components/molecules/SpinnerButton';
import { useRequestOpenReportingPasswordResetMutation } from 'src/graphql';
import { useIsMobile } from 'src/hooks/useIsMobile';
import { FormInputVariant, FormState } from 'src/types';
import { isEmail } from 'validator';
import {
  Description,
  HeaderWrapper,
  Heading,
  IconContainer,
  ResultDescription,
} from '../../organisms/Reporting/ReportingForgotPassword/ReportingForgotPassword.styles';
import {
  ButtonContainer,
  FormContainer,
  InputsContainer,
} from '../../organisms/Reporting/ReportingLogin/ReportingLogin.styles';
import { OpenReportingContainer } from '../OpenReportingContainer/OpenReportingContainer';

interface FormValues {
  email: string;
  vaultId: string;
}

interface Props {
  organisation: string;
  logo: string;
}

export const OPRForgotPassword = ({ organisation, logo }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const emptyStateSrc = theme.isVaultVariant
    ? '/assets/empty-state.svg'
    : '/assets/empty-state-atlas.svg';
  const isMobile = useIsMobile();
  const {
    register,
    handleSubmit,
    formState: { isDirty, isValid },
  } = useForm<FormValues>({
    mode: 'onChange',
  });

  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const requestPasswordReset = useRequestOpenReportingPasswordResetMutation({
    onSettled() {
      setHasSubmitted(true);
    },
  });

  const onSubmit = async ({ email, vaultId }: FormValues) => {
    requestPasswordReset.mutate({
      input: { email, vaultId, slug: organisation },
    });
  };

  return (
    <>
      {hasSubmitted ? (
        <OpenReportingContainer organisation={organisation} logo={logo}>
          <IconContainer>
            <img src={emptyStateSrc} alt="Empty state" width="100%" />
          </IconContainer>
          <Heading data-testid="reporting-send-email-result-heading">
            {t('public.Password.passwordResetRequestedHeader')}
          </Heading>
          <ResultDescription data-testid="reporting-send-email-result-description">
            {t('public.Password.passwordResetRequestedDescription')}
          </ResultDescription>
        </OpenReportingContainer>
      ) : (
        <OpenReportingContainer organisation={organisation} logo={logo}>
          <FormContainer onSubmit={handleSubmit(onSubmit)}>
            <InputsContainer>
              <HeaderWrapper data-testid="reporting-send-reset-password-email-header">
                {t('reporting.ReportingForgotPassword.heading')}
              </HeaderWrapper>
              <Description data-testid="reporting-send-reset-password-email-description">
                {t('public.Password.forgottenPasswordInstructions')}
              </Description>
              <FormInput
                variant={FormInputVariant.INPUT}
                state={FormState.DEFAULT}
                label={t('public.Password.yourVaultId')}
                placeholder={t('public.Password.yourVaultIdPlaceholder', {
                  exampleId: '123456-ABCDE',
                })}
                type="text"
                {...register('vaultId', {
                  required: true,
                })}
              />
              <FormInput
                variant={FormInputVariant.INPUT}
                state={FormState.DEFAULT}
                label={t('reporting.ReportingLogin.emailLabel')}
                placeholder={'email@address.com'}
                type="text"
                {...register('email', {
                  required: true,
                  validate: (value) => isEmail(value),
                })}
              />
            </InputsContainer>

            <ButtonContainer isMobile={isMobile}>
              <LinkButton
                to={`/${organisation}/open-reporting/login`}
                variant="secondary"
              >
                {t('reporting.ReportingConnect.cancelButton')}
              </LinkButton>
              <SpinnerButton
                variant="primary"
                size="medium"
                type="submit"
                width="100%"
                disabled={!isDirty || !isValid}
              >
                {t('reporting.ReportingForgotPassword.sendLink')}
              </SpinnerButton>
            </ButtonContainer>
          </FormContainer>
        </OpenReportingContainer>
      )}
    </>
  );
};
